'use client';

import { useEffect, type MutableRefObject } from 'react';
import {
  cloudPointer,
  updateCloudPointer,
  type CloudPointer,
} from './cloudPointer';

const CONTROLS =
  'a, button, input, textarea, select, summary, [role="button"], [contenteditable]:not([contenteditable="false"]), #leva__root, [class*="leva-c-"]';

export const useCloudPointer = (
  workerRef: MutableRefObject<Worker | null>
): void => {
  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let pending: CloudPointer = { ...cloudPointer, movedAt: 0, active: false };
    let previous: { x: number; y: number; time: number } | null = null;
    let raf = 0;
    const publish = () => {
      raf = 0;
      updateCloudPointer(pending);
      workerRef.current?.postMessage({
        type: 'cloud-pointer',
        pointer: pending,
      });
    };
    const clear = () => {
      cancelAnimationFrame(raf);
      previous = null;
      pending = {
        ...pending,
        active: false,
        movedAt: 0,
        speed: 0,
        stroke: pending.stroke + 1,
      };
      publish();
    };
    const move = (event: PointerEvent) => {
      if (
        event.pointerType !== 'mouse' ||
        reducedMotion.matches ||
        document.hidden ||
        event.defaultPrevented ||
        !(event.target instanceof Element) ||
        event.target.closest(CONTROLS)
      ) {
        if (pending.active) clear();
        return;
      }
      const time = performance.now();
      const distance = previous
        ? Math.hypot(event.clientX - previous.x, event.clientY - previous.y)
        : 0;
      const seconds = previous
        ? Math.max(0.001, (time - previous.time) / 1000)
        : 1;
      pending = {
        ...pending,
        x: event.clientX / Math.max(1, window.innerWidth),
        y: 1 - event.clientY / Math.max(1, window.innerHeight),
        movedAt: distance > 0 ? Date.now() : pending.movedAt,
        speed:
          distance > 0 ? Math.min(6000, distance / seconds) : pending.speed,
        active: true,
      };
      if (distance > 0 || !previous)
        previous = { x: event.clientX, y: event.clientY, time };
      if (!raf) raf = requestAnimationFrame(publish);
    };
    window.addEventListener('pointermove', move, { passive: true });
    window.addEventListener('pointercancel', clear, { passive: true });
    window.addEventListener('blur', clear);
    window.addEventListener('resize', clear, { passive: true });
    window.addEventListener('scroll', clear, { passive: true, capture: true });
    document.documentElement.addEventListener('pointerleave', clear);
    document.addEventListener('visibilitychange', clear);
    reducedMotion.addEventListener('change', clear);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointercancel', clear);
      window.removeEventListener('blur', clear);
      window.removeEventListener('resize', clear);
      window.removeEventListener('scroll', clear, true);
      document.documentElement.removeEventListener('pointerleave', clear);
      document.removeEventListener('visibilitychange', clear);
      reducedMotion.removeEventListener('change', clear);
      clear();
    };
  }, [workerRef]);
};
